import { COMPARATOR_TYPES, CONDITION_VARIANTS, QUESTION_TYPES } from '@/store';
import isNil from 'lodash/isNil';
import Utils from '@/providers/Utils';
import { isJobQuestionAnswered } from '@/helpers/jobQuestionsHelper';
import sumBy from 'lodash/sumBy';
import toNumber from 'lodash/toNumber';

export const VISIBILITY_CONDITION_TYPE_AND = 'AND';
export const VISIBILITY_CONDITION_TYPE_OR = 'OR';

export const isQuestionUnknown = (question) =>
  question &&
  Array.isArray(question.variables) &&
  question.variables.every((v) => v.response && v.response.is_unknown === 1);

export const isDeclined = (question) => {
  return question.variables.some((v) => {
    return v.response && v.response.declined;
  });
};

export const isCompleted = (
  question,
  isQuestionnaireSupportJobPreferences = false
) => {
  if (question.type === QUESTION_TYPES.SLIDER && isQuestionUnknown(question)) {
    if (!isQuestionnaireSupportJobPreferences || !question.relatedJobQuestion) {
      return true;
    }
    return isJobQuestionAnswered(question.relatedJobQuestion);
  }
  let isQuestionAnswered = false;

  if (
    question.type === QUESTION_TYPES.SELECT_ONE ||
    question.type === QUESTION_TYPES.SELECT_ANY
  ) {
    isQuestionAnswered =
      question.variables.some(
        (v) => v.response && parseInt(v.response.value) === 1
      ) || isDeclined(question);
  } else if (question.type === QUESTION_TYPES.SLIDER) {
    if (question.calculate_totals && isPercentageQuestion(question)) {
      isQuestionAnswered =
        (!isInvalid(question) &&
          question.variables.some((v) => {
            return v.response.value !== null && v.response.value !== undefined;
          })) ||
        isDeclined(question);
    } else {
      isQuestionAnswered =
        question.variables.every((v) => {
          return (
            v.response.value !== null &&
            v.response.value !== undefined &&
            !v.response.invalid
          );
        }) || isDeclined(question);
    }
  } else {
    isQuestionAnswered =
      question.variables.some((v) => {
        return (
          v.response &&
          v.response.value !== null &&
          v.response.value !== undefined &&
          !v.response.invalid
        );
      }) ||
      isDeclined(question) ||
      question.type === QUESTION_TYPES.GEOGRAPHY_ICO_WRAPPER;
  }

  if (!isQuestionnaireSupportJobPreferences || !question.relatedJobQuestion) {
    return isQuestionAnswered;
  }
  return (
    isQuestionAnswered && isJobQuestionAnswered(question.relatedJobQuestion)
  );
};

export const isIcoCompleted = (
  question,
  isQuestionnaireSupportJobPreferences = false
) => {  
    if (!isQuestionnaireSupportJobPreferences || !question.relatedJobQuestion) {
      return true;
    }
    return isJobQuestionAnswered(question.relatedJobQuestion);
};

export const isCompletedMultiVariable = (
  question,
  isQuestionnaireSupportJobPreferences = false
) => {
  const isQuestionAnswered =
    question.variables.every((v) => {
      return (
        v.response &&
        v.response.value !== null &&
        v.response.value !== undefined &&
        !v.response.invalid
      );
    }) || isDeclined(question);

  if (!isQuestionnaireSupportJobPreferences || !question.relatedJobQuestion) {
    return isQuestionAnswered;
  }

  return (
    isQuestionAnswered && isJobQuestionAnswered(question.relatedJobQuestion)
  );
};

export const isInvalid = (question) => {
  return question.variables.some((v) => {
    return v.response && v.response.invalid;
  });
};

export const getSectionCompletedOrDeclinedCount = (
  section,
  variables,
  isQuestionnaireSupportJobPreferences = false
) => {
  return section.questionSections.filter((qs) => {
    return (
      !!isApplicableQuestion(qs.question, variables) &&
      isCompleted(qs.question, isQuestionnaireSupportJobPreferences) &&
      !isInvalid(qs.question)
    );
  }).length;
};

export const getSectionApplicableCount = (section, variables) => {
  return section.questionSections.filter((qs) => {
    return isApplicableQuestion(qs.question, variables);
  }).length;
};

const isQuestionVarMeetsUseNoCondition = (val) =>
  val !== null && val !== undefined && parseInt(val) === 0;

const isQuestionVarMeetsBoolCondition = (val) => val && parseInt(val) === 1;

const isQuestionVarMeetsSliderCondition = (val, comparator) => {
  const { type, first_value, second_value } = comparator || {};
  if (!val || !type || isNil(first_value)) {
    return false;
  }
  const intVal = parseInt(val);
  switch (type) {
    case COMPARATOR_TYPES.LOWER:
      return intVal < parseInt(first_value);
    case COMPARATOR_TYPES.GRATER:
      return intVal > parseInt(first_value);
    case COMPARATOR_TYPES.EQUAL:
      return intVal === parseInt(first_value);
    case COMPARATOR_TYPES.BETWEEN_EXCLUSIVE:
      return (
        !isNil(second_value) &&
        intVal > parseInt(first_value) &&
        intVal < parseInt(second_value)
      );
    case COMPARATOR_TYPES.BETWEEN_INCLUSIVE:
      return (
        !isNil(second_value) &&
        intVal >= parseInt(first_value) &&
        intVal <= parseInt(second_value)
      );
  }
};

const isQuestionMeetsAndCondition = (
  conditionVarIds,
  allVariables,
  comparatorValues,
  conditionVariablesParams
) => {
  return allVariables
    .filter(({ id }) => conditionVarIds.includes(id))
    .every(({ id, response }) =>
      comparatorValues[id]
        ? isQuestionVarMeetsSliderCondition(
            response.value,
            comparatorValues[id]
          )
        : conditionVariablesParams[id] &&
          conditionVariablesParams[id].use_no === 1
        ? isQuestionVarMeetsUseNoCondition(response.value)
        : isQuestionVarMeetsBoolCondition(response.value)
    );
};

const isQuestionMeetsOrCondition = (
  conditionVarIds,
  allVariables,
  comparatorValues,
  conditionVariablesParams
) => {
  return allVariables
    .filter(({ id }) => conditionVarIds.includes(id))
    .some(({ id, response }) =>
      comparatorValues[id]
        ? isQuestionVarMeetsSliderCondition(
            response.value,
            comparatorValues[id]
          )
        : conditionVariablesParams[id] &&
          conditionVariablesParams[id].use_no === 1
        ? isQuestionVarMeetsUseNoCondition(response.value)
        : isQuestionVarMeetsBoolCondition(response.value)
    );
};

export const isApplicableQuestion = (question, variables) => {
  if (!question.condition || !Array.isArray(question.condition)) {
    return true;
  }
  const visibilityCondition = getConditionByVariant(
    question.condition,
    CONDITION_VARIANTS.VISIBILITY
  );
  if (
    !visibilityCondition ||
    !visibilityCondition.condition_type ||
    !visibilityCondition.condition_variables ||
    !Array.isArray(visibilityCondition.condition_variables)
  ) {
    return true;
  }
  const {
    condition_type: type,
    condition_variables: conVarIds,
    comparator_values: comparatorValues = {},
    condition_variables_params: conditionVariablesParams = {},
  } = visibilityCondition;
  if (type === VISIBILITY_CONDITION_TYPE_AND)
    return isQuestionMeetsAndCondition(
      conVarIds,
      variables,
      comparatorValues,
      conditionVariablesParams
    );
  if (type === VISIBILITY_CONDITION_TYPE_OR)
    return isQuestionMeetsOrCondition(
      conVarIds,
      variables,
      comparatorValues,
      conditionVariablesParams
    );
  else return true;
};

export const incompletedQuestions = (
  questions,
  variables,
  isQuestionnaireSupportJobPreferences = false
) => {
  return questions.filter((q) => {
    return (
      !!isApplicableQuestion(q, variables) &&
      (!isCompleted(q, isQuestionnaireSupportJobPreferences) || isInvalid(q))
    );
  });
};

const getConditionByVariant = (conditionsArray, variant) =>
  conditionsArray.find((cond) => cond.variant === variant);

export const isVariableMatchVisibilityCondition = (variable, allVariables) => {
  if (!variable.condition || !Array.isArray(variable.condition)) return true;
  const visibilityCondition = getConditionByVariant(
    variable.condition,
    CONDITION_VARIANTS.VISIBILITY
  );
  if (!visibilityCondition || !visibilityCondition.variable_id) return true;
  const intConditionId = parseInt(visibilityCondition.variable_id);
  return allVariables.some(
    ({ id, response }) =>
      intConditionId === parseInt(id) &&
      response.value &&
      parseInt(response.value) === 1
  );
};

export const replaceCustomTagsWithDataAndHtmlTags = (
  text,
  questionnaire = {}
) => {
  const { period } = questionnaire;
  const arrayOfData = [
    ['[PERIOD]', period],
    ['[PERIOD_LONG]', `1/1/${period} - 12/31/${period}`],
  ];
  const res = Utils.replaceSpecialSymbolsInString(text, arrayOfData);
  return Utils.replaceUnderlineMarkersWithTags(res);
};

export const isQuestionAvailableOnResultsForQuestionnaire = (
  questionId,
  questionnaire
) => {
  return questionnaire.sections.some((section) =>
    section.questionSections.some(
      ({ question_id, is_available_on_results,is_included_in_default_report,report_order }) =>
        parseInt(question_id) === parseInt(questionId) &&
        (is_available_on_results || (is_included_in_default_report) && report_order==0)
    )
  );
};

export const isPercentageQuestion = (question) => {
  if (!question || !Array.isArray(question.variables)) return false;
  const suffix = question.variables[0].suffix || '';
  return suffix.trim() === '%';
};

export const calculateVariablesTotal = (variables) =>
  sumBy(variables, (v) => {
    const val = v.response.value;
    return val ? toNumber(val) : 0;
  });

const isQuestionHasValidationCondition = ({
  type,
  condition,
  calculate_totals,
}) =>
  type === QUESTION_TYPES.SLIDER &&
  Array.isArray(condition) &&
  calculate_totals;

const isResponseConditionValid = (condition) => {
  const responseCondition = getConditionByVariant(
    condition,
    CONDITION_VARIANTS.RESPONSE_VALIDATION
  );
  return (
    !!responseCondition &&
    Array.isArray(responseCondition.condition_variables) &&
    responseCondition.condition_variables.length > 0 &&
    !!responseCondition.comparator_values
  );
};

export const isVariableHasNoResponse = (v) =>
  !v.response || v.response.value === null || v.response.value === undefined;

const isQuestionUnanswered = (question) =>
  question.variables.every(isVariableHasNoResponse);

export const sliderQuestionConditionValidation = (
  question,
  visibleQuestionVariables,
  allVariables
) => {
  const defaultResult = { isValid: true };
  if (!question.condition || !Array.isArray(question.condition)) {
    return defaultResult;
  }
  const condition = getConditionByVariant(
    question.condition,
    CONDITION_VARIANTS.RESPONSE_VALIDATION
  );
  if (
    !condition ||
    !Array.isArray(condition.condition_variables) ||
    condition.condition_variables.length === 0 ||
    !condition.comparator_values
  ) {
    return defaultResult;
  }
  const varIdToCompare = parseInt(condition.condition_variables[0]);
  const variableToCompare = allVariables.find(
    ({ id }) => id === varIdToCompare
  );
  if (!variableToCompare) return defaultResult;
  const {
    response: compareResponse = {},
    input_label: compareLabel,
  } = variableToCompare;
  const { value: compareResponseValue = null } = compareResponse;
  if (compareResponseValue === undefined || compareResponseValue === null) {
    return {
      isValid: false,
      error: `Before answering this question, please provide an answer for: ${compareLabel}`,
    };
  }
  const questionTotal = calculateVariablesTotal(visibleQuestionVariables);
  const { type = null } = condition.comparator_values[varIdToCompare];
  let error = null;
  switch (type) {
    case COMPARATOR_TYPES.LOWER:
      if (questionTotal >= compareResponseValue) {
        error = `Question total must be lower than value of the '${compareLabel}'`;
      }
      break;
    case COMPARATOR_TYPES.LOWER_OR_EQUAL:
      if (questionTotal > compareResponseValue) {
        error = `Question total must be lower or equal than value of the '${compareLabel}'`;
      }
      break;
    case COMPARATOR_TYPES.EQUAL:
      if (questionTotal !== compareResponseValue) {
        error = `Question total must be equal to the value of the '${compareLabel}'`;
      }
      break;
    case COMPARATOR_TYPES.GRATER_OR_EQUAL:
      if (questionTotal < compareResponseValue) {
        error = `Question total must be grater or equal than value of the '${compareLabel}'`;
      }
      break;
    case COMPARATOR_TYPES.GRATER:
      if (questionTotal <= compareResponseValue) {
        error = `Question total must be grater than value of the '${compareLabel}'`;
      }
      break;
  }
  if (error) {
    return {
      isValid: false,
      error: error,
    };
  }
  return defaultResult;
};

export const sliderVariableConditionValidation = (
  variable,
  value,
  allVariables
) => {
  const defaultResult = { isValid: true };
  if (!variable.condition || !Array.isArray(variable.condition)) {
    return defaultResult;
  }
  const condition = getConditionByVariant(
    variable.condition,
    CONDITION_VARIANTS.RESPONSE_VALIDATION
  );
  if (
    !condition ||
    !Array.isArray(condition.condition_variables) ||
    condition.condition_variables.length === 0 ||
    !condition.comparator_values
  ) {
    return defaultResult;
  }
  const varIdToCompare = parseInt(condition.condition_variables[0]);
  const variableToCompare = allVariables.find(
    ({ id }) => id === varIdToCompare
  );
  if (!variableToCompare) return defaultResult;
  const {
    response: compareResponse = {},
    input_label: compareLabel,
  } = variableToCompare;
  const { value: compareResponseValue = null } = compareResponse;
  if (compareResponseValue === undefined || compareResponseValue === null) {
    return {
      isValid: false,
      error: `Before setting up value, please provide an answer for: ${compareLabel}`,
    };
  }
  const { type = null } = condition.comparator_values[varIdToCompare];
  let error = null;
  switch (type) {
    case COMPARATOR_TYPES.LOWER:
      if (value >= compareResponseValue) {
        error = `Value must be lower than value of the '${compareLabel}'`;
      }
      break;
    case COMPARATOR_TYPES.LOWER_OR_EQUAL:
      if (value > compareResponseValue) {
        error = `Value must be lower or equal than value of the '${compareLabel}'`;
      }
      break;
    case COMPARATOR_TYPES.EQUAL:
      if (value !== compareResponseValue) {
        error = `Value must be equal to the value of the '${compareLabel}'`;
      }
      break;
    case COMPARATOR_TYPES.GRATER_OR_EQUAL:
      if (value < compareResponseValue) {
        error = `Value must be grater or equal than value of the '${compareLabel}'`;
      }
      break;
    case COMPARATOR_TYPES.GRATER:
      if (value <= compareResponseValue) {
        error = `Value must be grater than value of the '${compareLabel}'`;
      }
      break;
  }
  if (error) {
    return {
      isValid: false,
      error: error,
    };
  }
  return defaultResult;
};

export const validateVarResponseByOtherVarCondition = (
  { id: vId },
  val,
  allVariables
) => {
  const defaultResult = { isValid: true };
  const errors = allVariables.reduce((errors, variable) => {
    if (!variable.condition || !Array.isArray(variable.condition)) {
      return errors;
    }
    const condition = getConditionByVariant(
      variable.condition,
      CONDITION_VARIANTS.RESPONSE_VALIDATION
    );
    if (
      !condition ||
      !Array.isArray(condition.condition_variables) ||
      condition.condition_variables.length === 0 ||
      !condition.condition_variables.includes(vId) ||
      !condition.comparator_values
    ) {
      return errors;
    }
    const { input_label, response = {} } = variable;
    const { value: responseValue = null } = response;
    if (responseValue === null || responseValue === undefined) return errors;
    const { type = null } = condition.comparator_values[vId];
    switch (type) {
      case COMPARATOR_TYPES.LOWER:
        if (val <= responseValue) {
          errors.push(
            `Value must be grater than value of the '${input_label}'`
          );
        }
        break;
      case COMPARATOR_TYPES.LOWER_OR_EQUAL:
        if (val < responseValue) {
          errors.push(
            `Value must be grater or equal than value of the '${input_label}'`
          );
        }
        break;
      case COMPARATOR_TYPES.EQUAL:
        if (responseValue !== val) {
          errors.push(
            `Value must be equal to the value of the '${input_label}'`
          );
        }
        break;
      case COMPARATOR_TYPES.GRATER_OR_EQUAL:
        if (val > responseValue) {
          errors.push(
            `Value must be lower or equal than value of the '${input_label}'`
          );
        }
        break;
      case COMPARATOR_TYPES.GRATER:
        if (val >= responseValue) {
          errors.push(`Value must be lower than value of the '${input_label}'`);
        }
        break;
    }
    return errors;
  }, []);
  if (errors.length === 0) return defaultResult;
  return {
    isValid: false,
    errors,
  };
};

export const validateVarResponseByOtherQuestionCondition = (
  { id: vId },
  val,
  allQuestions
) => {
  const defaultResult = { isValid: true };
  const errors = allQuestions
    .filter(
      (question) =>
        isQuestionHasValidationCondition(question) &&
        isResponseConditionValid(question.condition) &&
        !isQuestionUnanswered(question)
    )
    .reduce((errors, question) => {
      const condition = getConditionByVariant(
        question.condition,
        CONDITION_VARIANTS.RESPONSE_VALIDATION
      );
      if (!condition.condition_variables.includes(vId)) return errors;
      const { prompt } = question;
      const total = calculateVariablesTotal(question.variables);
      const { type = null } = condition.comparator_values[vId];
      switch (type) {
        case COMPARATOR_TYPES.LOWER:
          if (val <= total) {
            errors.push(
              `Value must be grater than the total of the '${prompt}' question`
            );
          }
          break;
        case COMPARATOR_TYPES.LOWER_OR_EQUAL:
          if (val < total) {
            errors.push(
              `Value must be grater than the total of the '${prompt}' question`
            );
          }
          break;
        case COMPARATOR_TYPES.EQUAL:
          if (total !== val) {
            errors.push(
              `Value must be equal to the total of '${prompt}' question`
            );
          }
          break;
        case COMPARATOR_TYPES.GRATER_OR_EQUAL:
          if (val > total) {
            errors.push(
              `Value must be lower or equal to the total of '${prompt}' question`
            );
          }
          break;
        case COMPARATOR_TYPES.GRATER:
          if (val >= total) {
            errors.push(
              `Value must be lower than the total of '${prompt}' question`
            );
          }
          break;
      }
      return errors;
    }, []);
  if (errors.length === 0) return defaultResult;
  return {
    isValid: false,
    errors,
  };
};

export const compareQuestionVarsByOrder = (v1, v2) => {
  if (v1.order > v2.order) return 1;
  else if (v1.order === v2.order) return 0;
  return -1;
};
